import { Box, Typography, List, ListItem, Stack, styled } from "@mui/material";
import { useState, useMemo, useRef } from "react";
import { isBrowser, isAndroid } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useMenuStore } from "../../../store/menuState";
import { useOrderStore } from "../../../store/order";
import { useInfoStore } from "../../../store/userInfo";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useWindowDimensions } from "../../../App";
import { useAnalytics } from "../../../analytics/AnalyticsContext";
import Recommend from "./index";

const IMAGE_URL = process.env.REACT_APP_S3_IMAGE_PAPH;
const LazyPicture = styled(LazyLoadImage as any)`
  object-fit: cover;
  width: ${({ width }: { width?: string }) => (width ? `${width}rem` : "100%")};
  height: ${({ width }: { width?: string }) => (width ? `${width}rem` : "100%")};
  border-radius: 1.5rem;
`;

const RecommendView = (props: { itemDetail: any; }) => {
    const { itemDetail } = props;
    const { userInfo, currentLng } = useInfoStore();
    const { setCart } = useOrderStore();
    const { width } = useWindowDimensions();
    const storeId = userInfo?.storeId;
    const {t} =  useTranslation(`${storeId}`)
    const { isNativeFlag } = useMenuStore();
    const { recordEvent } = useAnalytics();
    const recommends = itemDetail?.recommends?.[0]?.recommendItems || null
    const [openRecommend, setOpenRecommend] = useState(false)
    const [recommendId, setRecommendId] = useState(0)
    const { menuDao } = useMenuStore()
    const [nativeImageVersion, setNativeImageVersion] = useState(0);

    const handleClickRecommend = (id: number) => {
      // if(!isBrowser && window.ReactNativeWebView !== undefined)
      //   window.ReactNativeWebView.postMessage(JSON.stringify({'type': 'play'}));
      
      // Pinpoint 이벤트 기록
      if (!isNativeFlag) {
        recordEvent('recommend_click', true, {
          recommendId: id,
          recommendName: t(`${storeId}:itemNames.${id}.${currentLng}`),
          parentItemId: itemDetail?.id || 0,
          parentItemName: itemDetail ? t(`${storeId}:itemNames.${itemDetail.id}.${currentLng}`) : 'unknown',
          storeId: storeId || 'unknown'
        });
      } else {
        if (window.Android !== undefined)
          window.Android.postMessage(JSON.stringify({ 
            type: 'attr', 
            data: {
              function: 'recommend_click', 
              recommendId: id, 
              recommendName: t(`${storeId}:itemNames.${id}.${currentLng}`), 
              parentItemId: itemDetail?.id || 0,
              parentItemName: itemDetail ? t(`${storeId}:itemNames.${itemDetail.id}.${currentLng}`) : 'unknown',
              storeId: storeId || 'unknown'
            } 
          }));
      }
      
      setRecommendId(id)
      setOpenRecommend(true)
    }

    /**
     * MIME 타입 추출 함수
     */
    const getMimeType = (imagePath: string): string => {
      const extension = imagePath.split(".").pop()?.toLowerCase();
      switch (extension) {
        case "jpg":
        case "jpeg":
          return "image/jpeg";
        case "png":
          return "image/png";
        case "webp":
          return "image/webp";
        case "gif":
          return "image/gif";
        default:
          return "application/octet-stream"; // 기본 MIME 타입
      }
    };
    
    /**
     * menuDao를 ID 기준으로 매핑
     */
    const menuMap = useMemo(() => {
      const map = new Map();
      menuDao.forEach((menu: any) => {
        map.set(menu.id, menu);
      });
      return map;
    }, [menuDao]);

    const defaultImage = "/assets/images/no_image.png";
    const base64CacheRef = useRef(new Map<string, string>());
    const getBase64ImageSrc = (imagePath: string | null): string => {
      if (!imagePath) return defaultImage;
      if (base64CacheRef.current.has(imagePath)) {
        return base64CacheRef.current.get(imagePath)!;
      }
      setTimeout(() => {
        const base64Image = window.Android?.getImageAsBase64(imagePath);
        if (base64Image) {
          const mimeType = getMimeType(imagePath);
          const src = `data:${mimeType};base64,${base64Image}`;
          base64CacheRef.current.set(imagePath, src);
          setNativeImageVersion(v => v + 1);
        }
      }, 0);
      return defaultImage;
    };

    if(!recommends) return null
    return (
      <Box sx={{width: '100%', mt: '2.0rem', height: '100%', overflow: 'hidden'}}>
        <Typography sx={{fontSize: '1.8rem', fontStyle: 'normal', fontWeight: '700', lineHeight: 'normal', textAlign: 'flex-start', height: '2.7rem', flexShrink: 0}}>Recommendation</Typography>
        <Box sx={{height: 'calc(100% - 2.7rem)', overflow: 'auto', padding: '1rem 0rem'}}>
          <List style={{ display: 'flex', flexDirection: 'row', flexFlow: "row wrap", padding: '1rem 0rem', gap: '0rem 3rem' }} >
            { recommends.map((item: {
              id: number; 
              images: any[]; 
              itemPrice: number;
              isPriceVisible?: boolean;
            }, idx: React.Key | null | undefined) => {

                // menuDao에서 item.itemId로 menuItem 찾기
                const menuItem = menuMap.get(item.id);
                const webImagePath = IMAGE_URL + `/${item.images[0]}`;
                const androidImagePath = menuItem?.images?.[0] ?? null;

                // Android 여부 판단 및 이미지 소스 결정
                // 이미지 소스 결정 Android : getBase64ImageSrc(androidImagePath) web : IMAGE_URL + `/${item.images[0]}`
                const imageSrc = isAndroid 
                  ? (androidImagePath ? getBase64ImageSrc(androidImagePath) : webImagePath)
                  : webImagePath;

                return (
                <ListItem key={idx} sx={{p: 0, width: '22.1rem', minHeight: '17.1rem' }}  onClick={() => handleClickRecommend(item.id)} >
                  <Box sx={{ minHeight: '16.1rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
                    {!item?.images[0]? 
                      // <LazyPicture
                      //   effect="opacity"
                      //   height={'181rem'}
                      //   src={noimage} // use normal <img> attributes as props
                      //   width={'231rem'}
                      // /> 
                      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'end', width: '22.1rem', height: '16.1rem', borderRadius: '1.5rem', background: '#ccc'}}>
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '19.6rem', height: '7.2rem', m: '4.5rem 1.3rem 4.4rem 1.2rem'}}>
                          <Typography sx={{fontSize: '1.8rem',  textAlign: 'left', fontWeight: '600', color: '#fff', lineHeight: '2.4rem', letterSpacing: '-.018rem' }}>
                            {t(`${storeId}:itemNames.${item.id}.${currentLng}`)}
                          </Typography>
                        </Box>
                      </Box>
                      :<>
                        {/* <IconButton aria-label="info" sx={{  position: 'absolute', width: '30rem', height: '30rem', left: '180rem', bottom: '15rem' }} onClick={(e) => handleSelectItem(e, item)}>
                          <img 
                            src={process.env.PUBLIC_URL+'/assets/images/ellipse.svg'}
                            width={'30rem'}
                            height={'30rem'}
                            alt="ellipse"
                          />
                          <img style={{position: 'absolute', left: '8rem', top: '8rem'}}
                            src={process.env.PUBLIC_URL+'/assets/images/plus.svg'}
                            width={'14rem'}
                            height={'14rem'}
                            alt="plus"
                          />
                        </IconButton> */}
                        <LazyPicture style={{height: '16.1rem', width: '22.1rem'}}
                          effect="opacity"
                          src={imageSrc}// {IMAGE_URL+`/${item.images[0]}`} // use normal <img> attributes as props
                        />
                      </>
                    }
                    <Stack sx={{ width: '22.1rem', minHeight: '9.7rem', justifyContent: 'flex-start'}}>
                      <Typography sx={{fontSize: '1.8rem', fontWeight: '600', fontStyle: 'normal', lineHeight: '2.4rem', letterSpacing: '-.018rem', textAlign: 'start', overflow: 'hidden'}}>{t(`${storeId}:itemNames.${item.id}.${currentLng}`)}</Typography>
                      <Typography sx={{fontSize: item.isPriceVisible ? '1.8rem': '1.6rem', fontWeight: '500', fontStyle: 'normal', lineHeight: '2.5rem', textAlign: 'start', height: '2.5rem'}}>{item.isPriceVisible ? `$${(item.itemPrice/100).toFixed(2)}`: "Priced by add-ons"}</Typography>
                    </Stack>
                  </Box>
                </ListItem>)
              })
            }
          </List>
        </Box>
      {openRecommend? <Recommend setOpenRecommend={setOpenRecommend} openRecommend={openRecommend} recommendId={recommendId} setRecommendId={setRecommendId}/>: <></>}
      </Box>
    )
}

export default RecommendView;